/**
 * Standardized report document names: [Month]_[Year]_[CLIENT NAME]_[ReportType]
 * Month is the calendar number with no zero-padding (7_2026, not 07_2026).
 * Report type labels match portal wording (State Reports, not Due Dates).
 * Upcoming uses the next calendar month (August selected → 9_2026).
 */
export const REPORT_DOCUMENT_TYPE_LABELS = {
    filed_transactions: 'CompletedFilings',
    completedFilings: 'CompletedFilings',
    completed_state_reports: 'Completed State Reports',
    outstanding_state_reports: 'Outstanding State Reports',
    upcoming_state_reports: 'Upcoming State Reports',
    non_premium_endorsements: 'NonPremium Endorsements',
    upcoming: 'Upcoming State Reports',
    completed: 'Completed State Reports',
    outstanding: 'Outstanding State Reports',
} as const;

const INVALID_FILENAME_CHARS = /[<>:"/\\|?*\u0000-\u001f]/g;

export function sanitizeFilenamePart(value: string | null | undefined): string {
    const cleaned = String(value || '')
        .replace(INVALID_FILENAME_CHARS, '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 150);
    return cleaned || 'CLIENT NAME';
}

function periodPart(value: number | string | null | undefined, min: number, max?: number): string {
    const parsed = Number.parseInt(String(value ?? ''), 10);
    if (!Number.isInteger(parsed) || parsed < min || (max != null && parsed > max)) {
        return 'Unknown';
    }
    return String(parsed);
}

function isUpcomingReportType(reportType: string): boolean {
    const key = String(reportType || '');
    return key === 'Upcoming State Reports'
        || REPORT_DOCUMENT_TYPE_LABELS[key as keyof typeof REPORT_DOCUMENT_TYPE_LABELS] === 'Upcoming State Reports';
}

function nextCalendarPeriod(month: number | string, year: number | string): { month: number | string; year: number | string } {
    const parsedMonth = Number.parseInt(String(month), 10);
    const parsedYear = Number.parseInt(String(year), 10);
    if (!Number.isInteger(parsedMonth) || parsedMonth < 1 || parsedMonth > 12
        || !Number.isInteger(parsedYear) || parsedYear < 2000) {
        return { month, year };
    }
    if (parsedMonth === 12) {
        return { month: 1, year: parsedYear + 1 };
    }
    return { month: parsedMonth + 1, year: parsedYear };
}

export function buildReportDocumentFilename({
    month,
    year,
    clientName,
    reportType,
    extension = 'pdf',
}: {
    month: number | string;
    year: number | string;
    clientName?: string | null;
    reportType: string;
    extension?: string;
}): string {
    const label = REPORT_DOCUMENT_TYPE_LABELS[reportType as keyof typeof REPORT_DOCUMENT_TYPE_LABELS] || reportType || 'Report';
    const ext = String(extension || 'pdf').replace(/^\./, '');
    const period = isUpcomingReportType(reportType)
        ? nextCalendarPeriod(month, year)
        : { month, year };
    return `${periodPart(period.month, 1, 12)}_${periodPart(period.year, 2000)}_${sanitizeFilenamePart(clientName)}_${label}.${ext}`;
}
